import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';
import searchIcon from '../../images/searchIcon.svg';
import magGlass from '../../images/magGlass.svg';
import arrowRight from '../../images/arrowRight.svg';
import externalLinkIcon from '../../images/externalLink.svg';
import errorIcon from '../../images/errorIcon.svg';
import hazardIcon from '../../images/hazardIcon.svg';
import config from '../../config/appConfig';
import { mockFuzzySearchResponse, filterMockData } from '../../data/mockData.js';
import { dummyPaFuzzySearchResponse } from '../../data/dummyPaData.js';
import { isLocalDevMode } from '../../utils/devMode.js';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { useLocation } from '../../contexts/LocationContext';
import { apicFetch } from '../../services/apiTokenService';
import starCircle from '../../images/starCircle.svg';
import fullRightArrow from '../../images/fullRightArrow.svg';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import { ERROR_TYPES, getErrorMessage } from '../errorMessages/errorMessages.constants';
import { classifyApiError } from '../../utils/apiErrorClassifier';


// Phase 4 (URL Parameter Removal): SearchBar never builds its own navigation URL — it always
// defers to the parent's `onSearch` callback (search.jsx / results.jsx), which pushes into
// SearchStateContext and calls a clean navigate() itself (see Phase 3/2). Previously this
// component ALSO had its own internal `navigate(url)` fallback that hand-built a
// `?query=&planCode=` query string, gated by a `disableNavigation` prop defaulting to `false`.
// Both real call sites always passed `disableNavigation={true}`, so that branch was already
// dead in practice — but it was a landmine: any future `<SearchBar>` usage that simply omitted
// the prop would have silently reintroduced query-string URLs and bypassed
// SearchStateContext entirely, undermining Phases 0-3. Removed outright rather than left
// dormant. `envPlanCode`/`disableNavigation` props, `resolvedEnvPlan`, and the react-router-dom
// imports they required are gone accordingly — nothing else in this component used them.
const SearchBar = ({ onSearch, placeholder = 'Search...', validateBeforeSearch, selectedPlan = '', initialValue = '', radiusMiles = '25' }) => {
  const { getCentralZip, planFeatures, environmentId, getErrorContactUsUrl } = usePlanFeatures();
  const { userLocation } = useLocation();

  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState({ provider: [], placesOfCare: [], specialty: [], allResult: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('allResult');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);
  const [showError, setShowError] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  // Keyed by tab.key -> the tab's DOM node, populated via the ref callback in the tab-list
  // JSX below. Lets handleTabKeyDown move focus directly to a sibling tab (ArrowLeft/Right/
  // Home/End) and lets scrollTabIntoView bring a tab into view programmatically, both needed
  // now that .suggestion-tabs can scroll horizontally (see styles.css) — previously the whole
  // row was clipped by the dropdown's overflow:hidden with no way to reach off-screen tabs at
  // all (the reported "can't swipe left to reach Types of care/Health conditions" bug).
  const tabRefs = useRef({});
  // Mobile-only horizontal scroll buttons for .suggestion-tabs (see
  // scrollTabsHeaderBy below) — the row was already made horizontally
  // scrollable via swipe/drag (see the .suggestion-tabs styles.css comment),
  // but on mobile that wasn't discoverable enough, so explicit left/right
  // arrow buttons were added overlaying the rightmost/leftmost chunk of the
  // header bar. This ref points at the actual scrollable element (.suggestion-tabs).
  const tabsScrollRef = useRef(null);
  // Tracks whether .suggestion-tabs currently has more content to scroll to on the left/
  // right, so the overlay arrow buttons (see updateTabsScrollState below) can hide
  // themselves at whichever end is already fully scrolled — e.g. the left arrow shouldn't
  // show at all until the row has actually been scrolled right some amount, and the right
  // arrow should disappear once scrolled all the way to the end. Previously both buttons
  // were always shown regardless of scroll position, which looked wrong when already at
  // an end (e.g. the left arrow visible while already fully scrolled left).
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  // Guards the selectedPlan-watcher effect below so it only reacts to a genuine later
  // change, not the unconditional run every effect gets on mount — see that effect for why.
  const isFirstSelectedPlanRun = useRef(true);
  // Set true only inside handleInputChange (real, manual keystrokes) — never by initialValue
  // hydration or any other programmatic query update. Gates the selectedPlan-watcher effect
  // below so it only ever re-fetches/re-opens the suggestions dropdown in response to an
  // actual later plan switch made *after* the user has manually typed a search term in this
  // session, not merely because a restored/pre-filled query + already-resolved selectedPlan
  // happened to coexist at load time (which used to silently pop the dropdown open on the
  // results page the instant it finished loading — see below).
  const hasUserTypedRef = useRef(false);

  // for testing 
  // Add global test function for debugging
  if (typeof window !== 'undefined') {
    window.testSearchAPI = async () => {
      try {
        await fetchSuggestions('test');
      } catch (err) {
        // Test failed
      }
    };
    
    // Add simple environment checker
    window.checkEnv = () => {
      return config.environment;
    };
  }

  // Log state changes
  useEffect(() => {
    // Dropdown state tracking removed
  }, [showDropdown, suggestions]);

  // Trap Tab/Shift+Tab within the suggestions dropdown while it's open, same pattern as
  // distancePanel.jsx/providerFilters.jsx's sidebar traps. Previously nothing intercepted
  // Tab at all here (handleKeyDown's 'navigateSuggestions' branch only handles
  // ArrowUp/ArrowDown/Escape) — so once focus reached the LAST focusable element inside the
  // dropdown (e.g. the close button, or the last suggestion item if a category has no
  // close/tab affordance after it), a further Tab press fell through to native browser tab
  // order and left the dropdown entirely, landing back on the search input or even further
  // out onto the rest of the page — exactly the reported "focus goes out of the modal...
  // goes to the search bar instead of staying inside the expanded modal." Recomputes the
  // focusable-elements list on every keydown (not just once when the dropdown opens) since
  // the set of suggestion items changes whenever the query/activeTab changes while it's open.
  useEffect(() => {
    if (!showDropdown || !dropdownRef.current) return undefined;

    const getFocusable = () => Array.from(
      dropdownRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;
      const focusableElements = getFocusable();
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    dropdownRef.current.addEventListener('keydown', handleTabKey);
    return () => {
      dropdownRef.current?.removeEventListener('keydown', handleTabKey);
    };
  }, [showDropdown, suggestions, activeTab]);

  // Update query when initialValue prop changes
  useEffect(() => {
    if (initialValue && initialValue.trim() && initialValue !== query) {
      setQuery(initialValue);
    }
  }, [initialValue]);

  // Reset active tab when suggestions change
  useEffect(() => {
    if (suggestions.allResult?.length > 0) {
      setActiveTab('allResult');
    }
  }, [suggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideInput = inputRef.current && !inputRef.current.contains(event.target);
      
      if (isOutsideDropdown && isOutsideInput) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Re-fetch suggestions when selectedPlan changes (if there's an active query)
  useEffect(() => {
    // Skip the run that fires on mount. Effects always execute once after the initial
    // render regardless of their dependency array — it's not exclusively for *changes*.
    // On the results page (and, on a returning visit, the search page too), SearchBar can
    // mount with a non-empty `query` (from the `initialValue` prop, e.g. the user's last
    // search) AND a truthy `selectedPlan` (already chosen/restored) at the same time. That
    // combination alone was enough to satisfy the condition below purely from initial
    // props — with no typing at all — silently opening the fuzzy-search suggestions
    // dropdown the instant the page loaded. This effect is only meant to catch a later,
    // genuine change to selectedPlan (e.g. switching plans mid-session while query text is
    // already present), so the first (mount) run is intentionally skipped.
    if (isFirstSelectedPlanRun.current) {
      isFirstSelectedPlanRun.current = false;
      return;
    }

    // Also require that the user has actually typed into this SearchBar during this
    // session (not just that a restored/pre-filled query happens to exist). A purely
    // async, load-time selectedPlan change (e.g. plan features resolving shortly after
    // mount) would otherwise still satisfy the mount-skip above once it's already been
    // consumed, and re-triggered the exact same "dropdown opens automatically on load"
    // bug this effect was originally meant to fix — just delayed by one tick instead of
    // happening on the literal first render.
    if (!hasUserTypedRef.current) {
      return;
    }

    if (query && query.length >= 3 && selectedPlan) {
      
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Debounce the re-fetch
      timeoutRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, 300);
    }
  }, [selectedPlan]);

  // Tags each raw suggestion item with which category array it came from
  // (`_sourceCategory`) so downstream rendering/click logic can tell what kind of
  // suggestion it is even when displayed under the merged "Top results" (allResult) tab,
  // where items from every category are mixed together and lose their original grouping.
  const tagCategory = (list, sourceCategory) => (list || []).map(item => ({ ...item, _sourceCategory: sourceCategory }));

  const categorizeResults = (results) => {
    // For now, we'll categorize based on specialty and provider info
    // This logic can be refined based on actual API response structure
    const provider = tagCategory(results["provider"], 'provider');
    const typesOfCare = tagCategory(results["typesOfCare"], 'typesOfCare');
    const whereToGetCare = tagCategory(results["whereToGetCare"], 'whereToGetCare');
    const healthConditions = tagCategory(results["healthConditions"], 'healthConditions');
    // allresult from the API is already the flat merged list, but it doesn't carry the
    // per-item _sourceCategory tags above, so rebuild it from the tagged arrays instead of
    // using results["allresult"] directly.
    const allResult = tagCategory(results["allresult"], 'allResult');

    return {
      provider,
      typesOfCare,
      whereToGetCare,
      allResult,
      healthConditions,
    };
  };

  const fetchSuggestions = async (searchQuery) => {

    if (searchQuery.length < 3) {
      setSuggestions({ provider: [], typesOfCare: [], whereToGetCare: [], allResult: [], healthConditions: [] });
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    setError('');

    // On localhost, use the hardcoded PA dummy fuzzy-search response instead of hitting
    // the real API through the /api-proxy dev proxy. Filter it loosely by the query text
    // against provider lastName so typing something unrelated to the fixture's Montgomery
    // County hospitals still shows an empty/appropriate result set rather than always
    // showing the same 5 providers regardless of what was typed.
    if (isLocalDevMode()) {
      console.log('🧪 Using dummy PA fuzzy search results (local dev mode)');
      const term = searchQuery.toLowerCase();
      const matches = (list) => (list || []).filter(item =>
        item.lastName?.toLowerCase().includes(term) ||
        item.specialty?.some(s => s.toLowerCase().includes(term))
      );

      const filteredProvider = tagCategory(matches(dummyPaFuzzySearchResponse.provider), 'provider');
      const filteredTypesOfCare = tagCategory(matches(dummyPaFuzzySearchResponse.typesOfCare), 'typesOfCare');
      const filteredWhereToGetCare = tagCategory(matches(dummyPaFuzzySearchResponse.whereToGetCare), 'whereToGetCare');

      // If nothing matched the typed term, just show the full dummy set so the
      // dropdown still demonstrates real content locally instead of going empty.
      const anyMatches = filteredProvider.length || filteredTypesOfCare.length || filteredWhereToGetCare.length;
      const categorizedResults = anyMatches
        ? {
            provider: filteredProvider,
            typesOfCare: filteredTypesOfCare,
            whereToGetCare: filteredWhereToGetCare,
            allResult: [...filteredProvider, ...filteredTypesOfCare, ...filteredWhereToGetCare],
            healthConditions: tagCategory(dummyPaFuzzySearchResponse.healthConditions, 'healthConditions')
          }
        : categorizeResults(dummyPaFuzzySearchResponse);

      setSuggestions(categorizedResults);
      setShowDropdown(true);
      setLoading(false);
      return;
    }

    // Construct environment-specific API URL and query params
    const searchConfig = config.SEARCH_API_CONFIG;

    // Get user location from context (app memory)
    const params = new URLSearchParams({
      envId: environmentId || searchConfig.envId,
      query: searchQuery,
      page: '0',
      size: '5',
      radiusMiles,
    });

    if (userLocation?.latitude != null && userLocation?.longitude != null) {
      params.set('latitude', userLocation.latitude);
      params.set('longitude', userLocation.longitude);
    } else {
      const centralZip = getCentralZip?.();
      if (centralZip) params.set('zip', centralZip);
    }
    
    if (selectedPlan && selectedPlan.trim() !== '') {
      // Only append planCode if it exists in the plan features collection for this environment
      const envPlanCodes = planFeatures?.planCode;
      const isValidForEnv = Array.isArray(envPlanCodes)
        ? envPlanCodes.includes(selectedPlan)
        : !!envPlanCodes; // scalar fallback
      if (isValidForEnv) {
        params.append('planCode', selectedPlan);
      }
    }
    let apiUrl;
    if (import.meta.env.DEV && window.location.hostname === 'localhost') {
      apiUrl = `/api-proxy${searchConfig.endpoint}?${params.toString()}`;
    } else {
      apiUrl = `${config.APIC_BASE_URL_API}${searchConfig.endpoint}?${params.toString()}`;
    }

    // Log the search request as GET
    console.log('🔎 SearchBar - Search request (GET):', {
      url: apiUrl,
      method: 'GET',

      timestamp: new Date().toISOString()
    });

    try {
      const response = await apicFetch(apiUrl);

      if (!response.ok) {
        console.error('❌ SearchBar - API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl
        });
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      console.log('🔎 SearchBar - Fuzzy search response:', {
        url: apiUrl,
        resultCount: (data || []).length,
        timestamp: new Date().toISOString()
      });

      const categorizedResults = categorizeResults(data || []);

      setSuggestions(categorizedResults);
      setShowDropdown(true);

    } catch (err) {
      console.error('❌ SearchBar - Fetch Error:', {
        message: err.message,
        url: apiUrl,
        timestamp: new Date().toISOString()
      });

      // Classify + surface the shared, plan-aware APIC/MCS backend error message
      // instead of a generic "Unable to load suggestions" string. See
      // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md.
      const classification = classifyApiError({ error: err });
      console.error('❌ SearchBar - Classified error:', classification);
      setError(getErrorMessage(ERROR_TYPES.API));
      setSuggestions({ provider: [], typesOfCare: [], whereToGetCare: [], allResult: [], healthConditions: [] });
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowError(false);
    hasUserTypedRef.current = true;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce API call
    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    
    if (!query.trim()) {
      setShowError(true);
      setError('Be sure to fill in the provider name, type of care or where to get care.');
      return;
    }

    // AC4: Validate plan selection before search
    if (validateBeforeSearch && !validateBeforeSearch()) {
      return;
    }

    // If a fuzzy search has already completed for the current input and it legitimately
    // returned zero results across every category (not just still loading, not an error
    // state, and not the pre-search/short-query state where the dropdown was never shown),
    // block the search — clicking the magnifying glass or pressing Enter shouldn't
    // navigate to a results page the user already knows will be empty.
    const hasNoFuzzyResults = showDropdown && !loading &&
      suggestions.allResult.length === 0 &&
      suggestions.provider.length === 0 &&
      suggestions.typesOfCare.length === 0 &&
      suggestions.whereToGetCare.length === 0 &&
      suggestions.healthConditions.length === 0;

    if (hasNoFuzzyResults) {
      return;
    }

    setShowDropdown(false);
    
    // Call onSearch callback if provided. onSearch (search.jsx's handleSearch /
    // results.jsx's handleNewSearch) owns all navigation — it updates SearchStateContext and
    // calls a clean navigate() itself. See the Phase 4 note above the component definition.
    if (onSearch) {
      onSearch(query.trim());
    }
  };
  // A suggestion represents a browsable "super category" (a type of care, place of care,
  // or health condition) rather than a specific named provider/specialty when the API marks
  // it with a `superCategory` string field. Per the backend's actual response shape, this is
  // NOT the same as simply belonging to the typesOfCare/whereToGetCare/healthConditions arrays
  // — those arrays also contain plain specialty suggestions (e.g. "ADDICTION PSYCHIATRY")
  // that have no `superCategory` field and should still search as regular specialty terms.
  // Clicking an actual super-category suggestion triggers a super-category search (same
  // mechanism as browseBy.jsx's category drill-downs) using its `superCategory` value,
  // instead of a plain free-text provider/specialty search.

  const handleSuggestionClick = (suggestion) => {
    // External-link suggestions (e.g. "LabCorp", "Quest Diagnostics") aren't a search at
    // all — selecting one (by click OR keyboard Enter) should open the same "you are now
    // leaving..." confirmation as clicking the suggestion's own external-link icon/text,
    // not attempt to build and run a text search. These items have no superCategory,
    // firstName/lastName, or specialty to build a search term from, so falling through to
    // the generic logic below had no real branch for them and silently landed on the
    // `'Unknown'` fallback — populating the search box with the literal text "Unknown" and
    // running a bogus query instead of ever opening the external link. This was the
    // reported "search bar is auto populated with unknown, and no external link is open"
    // bug when selecting LabCorp/Quest Diagnostics via keyboard (Tab reached this
    // suggestion's own wrapping element first, whose Enter handler called this function;
    // only a SECOND Tab press — landing on the separately-focusable nested `<a>` this used
    // to render — actually worked, correctly bypassing this function entirely via its own
    // onClick). Handling it here directly makes every suggestion a single, consistent
    // Tab stop with matching Enter/click behavior (see renderSuggestionItem below, which no
    // longer renders a second nested focusable `<a>` for this case).
    if (suggestion.externalLink) {
      setShowDropdown(false);
      setPendingExternalUrl(suggestion.externalLink);
      setExternalLinkModalOpen(true);
      return;
    }

    const isSuperCategory = typeof suggestion.superCategory === 'string' && suggestion.superCategory.trim().length > 0;
    const isSubcategory = suggestion.externalLinkValue && suggestion.externalLinkValue.trim().length > 0 && suggestion.externalLinkDesc && suggestion.externalLinkDesc.trim().length && suggestion.superCategory && suggestion.superCategory.trim().length > 0;
    // Build search term: for a subcategory drill-down (e.g. "Find more lab providers" under
    // "Labs (testing)", or "Routine dental care..." under "Dental care"), show the SPECIFIC
    // subcategory the user actually picked in the search box — not its parent superCategory
    // (previously `searchTerm` was always `suggestion.superCategory` whenever ANY
    // superCategory field was present, so picking a specific subcategory populated the
    // search box with the generic parent category label instead of the item the user
    // actually selected, the same class of "wrong kind of search" mismatch as the
    // external-link case above). The actual searchState sent to onSearch below still
    // correctly separates superCategory (parent) from subCategory (the specific pick) —
    // only the visible query text changes here.
    const fullName = `${suggestion.firstName || ''} ${suggestion.lastName || ''}`.trim();
    const searchTerm = isSubcategory
      ? suggestion.externalLinkValue
      : (isSuperCategory
        ? suggestion.superCategory
        : (fullName || (suggestion.specialty?.[0] || 'Unknown')));

    setQuery(searchTerm);
    setShowDropdown(false);
    
    // AC4: Validate plan selection before search
    if (validateBeforeSearch && !validateBeforeSearch()) {
      return;
    }
    
    // Call onSearch callback if provided — see note in handleSubmit above; onSearch owns
    // navigation, this component never builds its own URL. The second argument tells the
    // parent (search.jsx's handleSearch / results.jsx's handleNewSearch) to run this as a
    // super-category search (searchState.superCategory) rather than a plain-text query search
    // when the selected suggestion came from the typesOfCare/whereToGetCare/healthConditions
    // categories. The 3rd argument (subCategoryVal) is always the specific subcategory value
    // (suggestion.externalLinkValue) so search.jsx/results.jsx still get the parent
    // superCategory (via isSuperCategory ? query : '' — note `query` here is search.jsx's own
    // param name for this function's first argument, unrelated to this component's `query`
    // state) AND the specific subCategory, matching browseBy.jsx's drill-down convention —
    // this part of the data flow was already correct and is unchanged.
    if (onSearch) {
      if (isSubcategory) {
        onSearch(suggestion.superCategory, isSuperCategory, suggestion.externalLinkValue);
      } else {
        onSearch(searchTerm, isSuperCategory);
      }
    }

  };

  const handleExternalLinkConfirm = () => {
    if (pendingExternalUrl) {
      window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
    }
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };

  const handleExternalLinkCancel = () => {
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };

  const handleKeyDown = (e, action, data = null) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'closeDropdown') {
        closeSuggestions();
      } else if (action === 'selectSuggestion') {
        handleSuggestionClick(data);
      }
    } else if (action === 'navigateSuggestions') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextSuggestion = e.target.nextElementSibling;
        nextSuggestion?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevSuggestion = e.target.previousElementSibling;
        if (prevSuggestion) {
          prevSuggestion.focus();
        } else {
          // Focus back to the input
          inputRef.current?.focus();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        inputRef.current?.focus();
      }
    }
  };

  // Handle keyboard navigation on the main input
  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown' && showDropdown) {
      e.preventDefault();
      // Focus the first suggestion item
      const firstSuggestion = dropdownRef.current?.querySelector('.suggestion-item');
      firstSuggestion?.focus();
    } else if (e.key === 'Escape' && showDropdown) {
      e.preventDefault();
      setShowDropdown(false);
    }
  };

  const renderSuggestionItem = (suggestion, index) => {
    const isSuperCategory = typeof suggestion.superCategory === 'string' && suggestion.superCategory.trim().length > 0;
    const fullName = `${suggestion.firstName || ''} ${suggestion.lastName || ''}`.trim();
    const specialty = suggestion.specialty?.length > 0 
      ? suggestion.specialty.join(', ') 
      : (suggestion.specialty?.[0] || 'N/A');
    const location = `${suggestion.provPhyCity || ''}, ${suggestion.provPhyState || ''}`.trim();

    // Super-category nodes (e.g. "Behavioral and mental health...") come back with empty
    // firstName/lastName/specialty, so they  must take priority here — otherwise fullName ||
    // specialty falls through to the 'N/A' placeholder instead of the actual category label.
    let displayText = isSuperCategory ? suggestion.superCategory : (fullName || specialty);
    if (suggestion.externalLink){
      displayText = suggestion.externalLinkValue || displayText;
    } else if (isSuperCategory && suggestion.externalLinkValue) {
      displayText = suggestion.externalLinkValue;
    }
    let searchDesc = fullName && specialty; // Only show specialty in description if we have a name
    if (isSuperCategory && !suggestion.externalLinkDesc) {
      searchDesc = 'Browse this category';
    } else if (suggestion._sourceCategory === 'whereToGetCare') {
      if (suggestion.externalLinkDesc) {
        searchDesc = suggestion.externalLinkDesc;
      } else {
        searchDesc = 'Places that offer this type of care';
      }
    } else if (suggestion._sourceCategory === 'typesOfCare') {
      searchDesc = 'Providers that offer this type of care';
    } else if (suggestion._sourceCategory === 'healthConditions') {
      searchDesc = 'Providers that offer treatment for this health condition';
    } else if (fullName && specialty) {
      searchDesc = 'Provider';
    } else {
      if (suggestion.externalLinkDesc) {
        searchDesc = suggestion.externalLinkDesc;
      } else {
        searchDesc = 'Providers with this specialty';
      }
    }
    return (
      <div 
        key={`${suggestion.provId}-${index}`}
        className="suggestion-item"
        onClick={() => handleSuggestionClick(suggestion)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleKeyDown(e, 'selectSuggestion', suggestion);
          } else {
            handleKeyDown(e, 'navigateSuggestions', suggestion);
          }
        }}
        tabIndex={0}
        role="option"
        aria-label={suggestion.externalLink
          ? `Open ${displayText}, external link`
          : `Select ${displayText}${location ? ` in ${location}` : ''}`}
      >
        <div className="suggestion-content">
          <div className="suggestion-main">
            {/* Previously the externalLink branch rendered a nested `<a>` here — a second,
                independently-focusable/tabbable element INSIDE the already-focusable
                (tabIndex=0) wrapping .suggestion-item div above. That's why reaching "the
                interactable link" for LabCorp/Quest Diagnostics took two Tab presses
                instead of one, and why only clicking that inner `<a>` directly (whose
                onClick stopPropagation()-ed) actually opened the link — Tab/Enter on the
                OUTER div (the first, and for every other suggestion type the ONLY, tab
                stop) fell through to handleSuggestionClick's generic search-term logic
                instead, which had no real handling for this case (see its 'Unknown'
                fallback fix above). Rendering a plain, non-focusable <span> here instead —
                matching every other suggestion type below — makes the outer div the
                single tab stop for this suggestion too, with Enter/click both correctly
                routed through the same (now-fixed) handleSuggestionClick. */}
            {suggestion.externalLink
              ? <span><img src={starCircle} alt="" className="star-circle-icon" aria-hidden="true" style={{ marginRight: '10px' } } />{displayText}<img src={externalLinkIcon} alt="External link" className="suggestion-arrow" style={{ marginLeft: '10px' } } /></span>
              : !suggestion.externalLink && suggestion.externalLinkValue && suggestion.externalLinkDesc && suggestion.superCategory
                ? <span><img src={starCircle} alt="" className="star-circle-icon" aria-hidden="true" style={{ marginRight: '10px' } } />{displayText}<img src={arrowRight} alt="Go to result" className="suggestion-arrow" style={{ marginLeft: '8px' } } /></span>
              : <span>{displayText}<img src={arrowRight} alt="Go to result" className="suggestion-arrow" style={{ marginLeft: '8px' } } /></span>
            }
          </div>
          <div
            className="suggestion-description"
            style={
              (suggestion.externalLink || (!suggestion.externalLink && suggestion.externalLinkValue && suggestion.externalLinkDesc && suggestion.superCategory))
                ? { marginLeft: '35px' }
                : { marginLeft: '0px' }
            }
          >
            {<span >{searchDesc}</span>}
          </div>
        </div>
      </div>
    );
  };

  const closeSuggestions = () => {
    setShowDropdown(false);
  };

  // Brings a tab button fully into view within the now-horizontally-scrollable
  // .suggestion-tabs row (see styles.css) — a no-op if it's already fully visible.
  // 'nearest' avoids yanking the row further than necessary when the tab is already
  // partially visible, which would feel jumpy compared to a native swipe/scroll gesture.
  const scrollTabIntoView = (key) => {
    tabRefs.current[key]?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  };

  const activateTab = (key) => {
    setActiveTab(key);
    scrollTabIntoView(key);
  };

  // Mobile-only: scrolls the .suggestion-tabs row by HALF its total scrollable distance
  // (maxScrollLeft / 2) per click — two clicks reach the opposite edge. Previously this
  // jumped all the way to the far edge in one click, which could skip right past tabs in
  // the middle of the row when there were more than ~2 screens' worth of tabs to scroll
  // through.
  const scrollTabsHeaderToEdge = (direction) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const halfway = maxScrollLeft / 2;
    const target = direction === 'right'
      ? Math.min(el.scrollLeft + halfway, maxScrollLeft)
      : Math.max(el.scrollLeft - halfway, 0);
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  // Recomputes canScrollTabsLeft/Right from the row's actual current scroll position —
  // called on scroll, on resize (tab labels/viewport width can change how much overflows),
  // and once on mount/whenever the tab set changes (e.g. suggestion counts changing the
  // "(N)" labels' widths). A small 1px epsilon avoids float-rounding false positives that
  // would otherwise leave an arrow visibly stuck on-screen right at either end.
  const updateTabsScrollState = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const epsilon = 1;
    setCanScrollTabsLeft(el.scrollLeft > epsilon);
    setCanScrollTabsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon);
  };

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!showDropdown || !el) return undefined;

    updateTabsScrollState();
    el.addEventListener('scroll', updateTabsScrollState);
    window.addEventListener('resize', updateTabsScrollState);
    return () => {
      el.removeEventListener('scroll', updateTabsScrollState);
      window.removeEventListener('resize', updateTabsScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown, activeTab, suggestions]);

  // Dedicated keydown handler for the tab list, implementing the standard WAI-ARIA tabs
  // "manual activation" pattern (arrow keys move focus between tabs; Enter/Space activates
  // the focused one) — consistent with the Enter/Space-to-activate behavior this already had.
  // Added alongside the styles.css scrolling fix specifically so keyboard users have a direct
  // way to reach off-screen tabs (ArrowRight/Left, or jump straight there with Home/End)
  // instead of relying solely on repeated native Tab presses through every intervening tab.
  const handleTabKeyDown = (e, tabKey) => {
    const currentIndex = tabs.findIndex(t => t.key === tabKey);

    const focusTab = (index) => {
      const target = tabs[index];
      tabRefs.current[target.key]?.focus();
      scrollTabIntoView(target.key);
    };

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activateTab(tabKey);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTab((currentIndex + 1) % tabs.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTab((currentIndex - 1 + tabs.length) % tabs.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(tabs.length - 1);
    }
  };

  const tabs = [
    { key: 'allResult', label: 'Top results', count: suggestions.allResult?.length || 0 },
    { key: 'provider', label: 'Providers', count: suggestions.provider?.length || 0 },
    { key: 'typesOfCare', label: 'Types of care', count: suggestions.typesOfCare?.length || 0 },
    { key: 'whereToGetCare', label: 'Where to get care', count: suggestions.whereToGetCare?.length || 0 },
    { key: 'healthConditions', label: 'Health conditions', count: suggestions.healthConditions?.length || 0 }
  ];

  const renderTabContent = () => {
    const items = suggestions[activeTab] || [];
    
    if (items.length === 0) {
      // Exact copy per tab, per US acceptance criteria — NOT derived generically from
      // each tab's label (that previously produced "No where to get care found."/"No
      // result found." for the allResult tab, neither of which match the required
      // "No places for care found."/"No top results found." copy).
      const noResultsMessages = {
        allResult: 'No top results found.',
        provider: 'No providers found.',
        typesOfCare: 'No types of care found.',
        whereToGetCare: 'No places for care found.',
        healthConditions: 'No health conditions found.' 
      };
      return (
        <div 
          className="suggestion-no-results"
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
          // Announce the no-results message to screen reader users as soon as it
          // appears (e.g. after typing a query that matches nothing in this tab, or
          // switching to a tab that has no matches) instead of relying solely on
          // whatever focus happens to already be on — a plain tabpanel div isn't
          // itself an assertive/live region, so without this AT users had no
          // indication at all that their search came up empty in this tab.
          aria-live="polite"
        >
          {noResultsMessages[activeTab] || 'No results found.'}
        </div>
      );
    }

    return (
      <div 
        className="suggestion-tab-content"
        role="listbox"
        aria-labelledby={`tab-${activeTab}`}
      >
        {items.map((item, index) => renderSuggestionItem(item, index))}
      </div>
    );
  };

  return (
    <div className="mb30">
      {/* Dark overlay when suggestions are showing */}
      {showDropdown && <div className="search-overlay" onClick={closeSuggestions}></div>}
      
      <div id="searchHeader" className="">
        Provider info (required)
      </div>

      <form id="searchInputDiv" onSubmit={handleSubmit} role="search" aria-label="Provider search">
        <label htmlFor="searchInput" id="searchLabel">
          Fill in the provider name, type of care or where to get care.
        </label>

        <div className="search-input-wrapper" ref={inputRef}>
          <input
            ref={inputRef}
            type="text"
            id="searchInput"
            className={`col-6 ${showError ? 'error' : ''}`}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder=""
            role="combobox"
            aria-describedby={showError && error ? 'search-error-msg' : undefined}
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-controls="search-suggestions-listbox"
            aria-autocomplete="list"
            autoComplete="off"
            translate="no"
          />
          <button type="submit" className="search-button" aria-label="Search">
            <img src={magGlass} alt="Search" className="search-icon" />
          </button>

          {/* Suggestions Dropdown */}
          {showDropdown && (
            <div
              id="search-suggestions-listbox"
              className="suggestions-dropdown"
              ref={dropdownRef}
              role="tabpanel"
              aria-label="Search suggestions"
            >
              {loading && (
                <div className="suggestion-loading">Loading suggestions...</div>
              )}
              
              {!loading && error && (
                <div className="suggestion-error">{error}</div>
              )}
              
              {!loading && !error && (
                <>
                  {/* Tab Header */}
                  <div className="suggestion-tabs-header">
                    {/* Mobile-only overlay button covering the leftmost chunk of the header
                        bar — scrolls .suggestion-tabs all the way left. Hidden on desktop/
                        tablet via styles.css (the row doesn't need discoverable scroll
                        affordances there since more tabs typically fit without overflowing),
                        and hidden on mobile too whenever the row is already scrolled all the
                        way left (canScrollTabsLeft false) — see updateTabsScrollState. */}
                    {canScrollTabsLeft && (
                      <button
                        type="button"
                        className="suggestion-tabs-scroll-btn suggestion-tabs-scroll-btn--left"
                        onClick={() => scrollTabsHeaderToEdge('left')}
                        aria-label="Scroll search result categories left"
                      >
                        <img src={fullRightArrow} alt="" aria-hidden="true" />
                      </button>
                    )}
                    <div 
                      className={`suggestion-tabs ${canScrollTabsLeft ? 'suggestion-tabs--scroll-left-visible' : ''} ${canScrollTabsRight ? 'suggestion-tabs--scroll-right-visible' : ''}`}
                      role="tablist" 
                      aria-label="Search result categories"
                      ref={tabsScrollRef}
                    >
                      {tabs.map(tab => (
                        <div 
                          key={tab.key}
                          id={`tab-${tab.key}`}
                          ref={(el) => { tabRefs.current[tab.key] = el; }}
                          className={`suggestion-tab ${activeTab === tab.key ? 'active' : ''}`}
                          onClick={() => activateTab(tab.key)}
                          onKeyDown={(e) => handleTabKeyDown(e, tab.key)}
                          tabIndex={0}
                          role="tab"
                          aria-selected={activeTab === tab.key}
                          aria-label={`Switch to ${tab.label} tab, ${tab.count} results`}
                        >
                          <span className="tab-label">{tab.label} ({tab.count})</span>
                          {activeTab === tab.key && <div className="tab-indicator" />}
                        </div>
                      ))}
                    </div>
                    {/* Mobile-only overlay button covering the rightmost chunk of the header
                        bar — scrolls .suggestion-tabs all the way right. Hidden once the row
                        is already scrolled all the way right (canScrollTabsRight false). */}
                    {canScrollTabsRight && (
                      <button
                        type="button"
                        className="suggestion-tabs-scroll-btn suggestion-tabs-scroll-btn--right"
                        onClick={() => scrollTabsHeaderToEdge('right')}
                        aria-label="Scroll search result categories right"
                      >
                        <img src={fullRightArrow} alt="" aria-hidden="true" />
                      </button>
                    )}
                    <div 
                      className="suggestion-close" 
                      onClick={closeSuggestions}
                      onKeyDown={(e) => handleKeyDown(e, 'closeDropdown')}
                      tabIndex={0}
                      role="button"
                      aria-label="Close suggestions menu"
                    >
                      <span>×</span>
                    </div>
                  </div>
                  
                  {/* Tab Content */}
                  {renderTabContent()}
                </>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {showError && error && (
          <div id="search-error-msg" className="search-error-message" role="alert">
            <img src={hazardIcon} alt="" className="error-icon" />
            <span>
              {error}{' '}
              <a
                href={getErrorContactUsUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Need help? Contact us.
              </a>
            </span>
          </div>
        )}
      </form>

      {/* External Link Confirmation Modal — portaled to document.body. When SearchBar
          renders inside results.jsx, an ancestor (.results-page) has `transform:
          translateZ(0)` (see results/styles.css) to work around an Edge/Chromium
          compositor bug. That transform creates a new containing block for
          `position: fixed` descendants, so without a portal this modal's fixed
          overlay would be positioned relative to .results-page instead of the
          viewport — rendering it off-position/invisible. Matches the same portal
          pattern already used for this modal in providerSummary.jsx/
          providerServiceDetails.jsx/providerDetailsType2.jsx/hoverTooltip.jsx/
          stateOptions.jsx. */}
      {externalLinkModalOpen && createPortal(
        <ExternalLinkModal
          isOpen={externalLinkModalOpen}
          onConfirm={handleExternalLinkConfirm}
          onCancel={handleExternalLinkCancel}
          url={pendingExternalUrl}
        />,
        document.body
      )}
    </div>
  );
};

export default SearchBar;
